import {Group, Stack} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MODAL_LABELING_STRATEGIES} from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import LabelingStrategyInput from 'registry/labeling-strategies/LabelingStrategyInput';
import ProjectFolderField from 'registry/ProjectFolderField';
import RegistrySelector from 'registry/RegistrySelector';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';

import type {DraftRegistrationConfig} from './RegistrationConfigTypes';

export interface RegistrationConfigPaneProps {
  registrationConfig: DraftRegistrationConfig;
  onRegistrationConfigChange: (config: DraftRegistrationConfig) => void;
  /** When true, the registry selector is disabled (e.g., registry is derived from schema selection). */
  registryReadOnly?: boolean;
  /** When provided, restricts the naming strategy options to this list (e.g., from the selected schema's allowed strategies). */
  allowedLabelingStrategies?: Array<RegistryLabelingStrategy>;
}

/**
 * Form for configuring how entities will be created and registered.
 * Manages project folder, registry, and naming strategy selection.
 */
export default function RegistrationConfigPane({
  registrationConfig,
  onRegistrationConfigChange,
  registryReadOnly,
  allowedLabelingStrategies,
}: RegistrationConfigPaneProps): JSX.Element {
  const handleRegistryChange = useCallback(
    (e: {target: {value: {id: string} | null}}) => {
      onRegistrationConfigChange({...registrationConfig, registryId: e.target.value?.id ?? null});
    },
    [registrationConfig, onRegistrationConfigChange]
  );

  const handleNamingStrategyChange = useCallback(
    (strategy: RegistryLabelingStrategy) => {
      onRegistrationConfigChange({...registrationConfig, namingStrategy: strategy});
    },
    [registrationConfig, onRegistrationConfigChange]
  );

  const handleFolderChange = useCallback(
    (e: {target: {value: string}}) => {
      onRegistrationConfigChange({...registrationConfig, folderId: e.target.value || null});
    },
    [registrationConfig, onRegistrationConfigChange]
  );

  return (
    <Stack spacing="md">
      <ProjectFolderField
        folderId={registrationConfig.folderId}
        onChange={handleFolderChange}
        isRequired={BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES}
        labelStyle={LABEL_STYLES.LABEL}
      />

      <Group grow={true} align="flex-start" spacing="sm" noWrap={true}>
        <div>
          <FieldLabel label="Registry" labelStyle={LABEL_STYLES.LABEL} />
          {registryReadOnly && !registrationConfig.registryId ? (
            <SelectDropdown displayName="Select a schema first" disabled={true} menu={<></>} />
          ) : (
            <RegistrySelector
              registryId={registrationConfig.registryId}
              onChange={handleRegistryChange}
              disabled={registryReadOnly}
            />
          )}
        </div>

        <LabelingStrategyInput
          labelingStrategy={registrationConfig.namingStrategy}
          labelingStrategies={allowedLabelingStrategies ?? MODAL_LABELING_STRATEGIES}
          onChange={(strategy) => {
            handleNamingStrategyChange(strategy as RegistryLabelingStrategy);
          }}
        />
      </Group>
    </Stack>
  );
}
